import StatsIcon from '@/CustomIcons/StatsIcon'
import { Card } from '../../Card'
import { CardHeader } from '../../CardHeader'
import { Field } from '../../Field'
import { useUserContext } from '@/UserContext'
import UserIcon from '@/CustomIcons/UserIcon'
import { LocalVideoTrack } from 'livekit-client'
import { useRoomContext } from '@/RoomContext'
import { useState } from 'react'
import { MusePeer } from '@/services/RoomContextMusePeer'
import { SystemStats } from '@/functionalities/SystemStats/SystemStats'
import { Button } from '@/UIComponents2/Button'
import { Separator } from '../../Separator'

interface DeviceStatsSectionProps {
    systemStats: SystemStats
}

export function DeviceStatsSection({ systemStats }: DeviceStatsSectionProps) {
    const [userState] = useUserContext()
    const [roomState] = useRoomContext()

    const currVideoStreamInfo =
        (userState.mediaStreams.video instanceof LocalVideoTrack && userState.mediaStreams.videoInfo) || null
    const currDesktopStreamInfo =
        (userState.mediaStreams.desktop instanceof MediaStream && userState.mediaStreams.desktopInfo) || null

    return (
        <Card className="px-0 md:px-0">
            <CardHeader icon={StatsIcon} title="Device Statistics" className="px-[30px]" />
            <div className="flex flex-col gap-[16px] pt-[41px] w-full">
                <div className="flex items-center gap-[10px] px-[30px]">
                    <UserIcon />
                    <div className="font-[500] text-[16px]">You ({userState.profile?.username || ''})</div>
                </div>
                {userState.mediaStreams.audioInputs.map((a) => {
                    const settings = a.getSettings() as any
                    return (
                        <Field className="font-[300]" title={'Audio'}>
                            <div className="font-[400]">{a.label}</div>
                            <div className="font-[300] text-muse-fg/75">
                                - {settings.sampleRate}Hz, {settings.channelCount} channel(s)
                            </div>
                        </Field>
                    )
                })}
                {currVideoStreamInfo && (
                    <Field className="font-[300]" title={'Camera'}>
                        <div className="font-[400]">
                            <>
                                {currVideoStreamInfo.settings.width} x {currVideoStreamInfo.settings.height} @{' '}
                                {toFixedFallback(currVideoStreamInfo.settings.frameRate)} fps
                            </>
                        </div>
                    </Field>
                )}
                {currDesktopStreamInfo && (
                    <Field className="font-[300]" title={'Screen Share'}>
                        <div className="font-[400]">
                            <>
                                {currDesktopStreamInfo.settings.width} x {currDesktopStreamInfo.settings.height} @{' '}
                                {toFixedFallback(currDesktopStreamInfo.settings.frameRate)} fps
                            </>
                        </div>
                    </Field>
                )}
                {Object.keys(roomState.peers).map((sessionId, i) => {
                    const peer = roomState.peers[sessionId]
                    return <PeerStatsSection key={i} peer={peer} systemStats={systemStats} />
                })}
            </div>
        </Card>
    )
}

function PeerStatsSection({ peer, systemStats }: { peer: MusePeer; systemStats: SystemStats }) {
    const { username, remoteAudios: atracks, remoteVideo: vtrack, remoteDesktop: ptrack } = peer

    const vtrackInfo = (() => {
        if (!vtrack || !vtrack.track) {
            return null
        }
        try {
            return vtrack.track.getSettings()
        } catch (e) {
            return null
        }
    })()

    const ptrackInfo = (() => {
        if (!ptrack || !ptrack.track) {
            return null
        }
        try {
            return ptrack.track.getSettings()
        } catch (e) {
            return null
        }
    })()

    const [openDetailAccordions, set_openDetailAccordions] = useState<{ [key: string]: boolean }>({})

    return (
        <>
            <Separator />
            <div className="flex items-center gap-[10px] px-[30px]">
                <UserIcon />
                <div className="font-[500] text-[16px]">{username}</div>
            </div>
            {atracks.map((a) => {
                const settings = a.track.getSettings() as any
                const stats = (systemStats.mediaTrackStats || {})[a.track.id]
                const isDetailAccordionOpen = openDetailAccordions[a.track.id]

                return (
                    <>
                        <Field className="font-[300]" title="Audio">
                            <div className="font-[400]">{a.info.label}</div>
                            <div className="font-[300] text-muse-fg/75">
                                - {settings.sampleRate}Hz, {settings.channelCount} channel(s)
                            </div>
                            <div className="font-[300] text-muse-fg/75">- Network: {a.fromTopology}</div>
                            <div className="font-[300] text-muse-fg/75">
                                - Details:{' '}
                                <Button
                                    variant="rectangle"
                                    className="inline-flex items-center justify-center h-[25px] w-[25px] p-0 rounded-[8px]"
                                    onClick={() => {
                                        set_openDetailAccordions((s) => {
                                            const next = structuredClone(s)
                                            next[a.track.id] = !!!next[a.track.id]
                                            return next
                                        })
                                    }}
                                >
                                    {isDetailAccordionOpen ? '\u25B4' : '\u25BE'}
                                </Button>
                            </div>
                            {stats && openDetailAccordions[a.track.id] && (
                                <div className="font-[300] text-muse-fg/75 flex justify-center">
                                    <div className="grid grid-cols-2 gap-y-1 gap-x-4 w-full items-center">
                                        <div>Audio level</div>
                                        <div>{stats.audioLevel}</div>

                                        <div>Bytes received</div>
                                        <div>{stats.bytesReceived}</div>

                                        <div>Jitter</div>
                                        <div>{stats.jitter}</div>

                                        <div>Jitter buffer delay</div>
                                        <div>{stats.jitterBufferDelay}</div>

                                        <div>Jitter buffer emitted count</div>
                                        <div>{stats.jitterBufferEmittedCount}</div>

                                        <div>Packets discarded</div>
                                        <div>{stats.packetsDiscarded}</div>

                                        <div>Packets lost</div>
                                        <div>{stats.packetsLost}</div>

                                        <div>Packets received</div>
                                        <div>{stats.packetsReceived}</div>

                                        <div>Removed samples</div>
                                        <div>{stats.removedSamplesForAcceleration}</div>

                                        <div>Total audio energy</div>
                                        <div>{stats.totalAudioEnergy}</div>

                                        <div>Ping host (round trip)</div>
                                        {systemStats.pingMeasurement?.sessionId === peer.sessionId && (
                                            <div>{systemStats.pingMeasurement.roundTripLatencyMs || '-'} ms</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Field>
                    </>
                )
            })}
            {vtrackInfo && (
                <Field className="font-[300]" title="Camera">
                    <div className="font-[400]">
                        {vtrackInfo.width} x {vtrackInfo.height} @ {toFixedFallback(vtrackInfo.frameRate)} fps
                    </div>
                </Field>
            )}
            {ptrackInfo && (
                <Field className="font-[300]" title="Screen Share">
                    <div className="font-[400]">
                        {ptrackInfo.width} x {ptrackInfo.height} @ {toFixedFallback(ptrackInfo.frameRate)} fps
                    </div>
                </Field>
            )}
        </>
    )
}

function toFixedFallback(v: any, places?: number): string {
    if (!v || isNaN(v) || typeof v.toFixed !== 'function') {
        return '-'
    }

    return v.toFixed(places || 2) || '-'
}
